(() => {
  const TAG_NAME = 'vl-support-chat-launcher';
  const WINDOW_TAG_NAME = 'vl-support-chat-window';
  const ZENDESK_CLOSE_EVENT = 'vl:support-chat-zendesk-close';
  const ZENDESK_SCRIPT_ID = 'ze-snippet';
  const ZENDESK_SCRIPT_KEY = 'ee2dcbd5-badf-4ede-8fd9-6ccb14bce984';
  const ZENDESK_SCRIPT_URL = `https://static.zdassets.com/ekr/snippet.js?key=${ZENDESK_SCRIPT_KEY}`;
  const ZENDESK_LOAD_TIMEOUT = 10000;
  const QUICK_CLOSE_THRESHOLD_MS = 3000;
  const ZENDESK_CUSTOMIZATION = {
    theme: {
      primary: '#FFFFFF',
      onPrimary: '#1F1F1F',
      message: '#FDBD03',
      onMessage: '#1F1F1F',
      action: '#FDBD03',
      onAction: '#1F1F1F',
      businessMessage: '#FFFFFF',
      onBusinessMessage: '#1F1F1F',
      background: '#F8F9FD',
      onBackground: '#1F1F1F',
      conversationListBackground: '#F8F9FD',
      onConversationListBackground: '#1F1F1F',
      error: '#D92D20',
      onError: '#FFFFFF',
      notify: '#FDBD03',
      onNotify: '#1F1F1F',
      onSecondaryAction: '#1F1F1F',
    },
    common: {
      contentScale: 100,
      hideHeader: true,
      stylingPreset: 'default',
    },
    conversationList: {
      hideHeader: true,
      hideNewConversationButton: false,
      avatar: {
        hidden: false,
        size: 48,
      },
      hideTimestamp: false,
      hideMessagePreview: false,
      hideDivider: false,
      groupByTimestamp: false,
    },
    messageLog: {
      hideHeader: true,
      avatar: {
        hidden: true,
        position: 'top-side',
        size: 24,
      },
      businessMessage: {
        bubbleMaxWidth: 80,
        hideBubble: false,
      },
      userMessage: {
        bubbleMaxWidth: 80,
        hideBubble: false,
      },
    },
    position: {
      side: 'right',
      offset: {
        web: { horizontal: 20, vertical: 20 },
        mobile: { horizontal: 16, vertical: 16 },
      },
    },
  };

  if (customElements.get(TAG_NAME)) return;

  let zendeskCloseHandlerBound = false;
  let zendeskCustomizationApplied = false;
  let zendeskLoadPromise = null;

  // ===== PLUNO TEMP START: body scroll-lock compatibility =====
  // While the Zendesk/Pluno chat is open, preserve its body lock. When it is
  // closed, CSS overrides Pluno's residual fixed styles (see CSS marker).
  const PLUNO_BODY_LOCK_CLASS = 'vl-support-chat-pluno-active';
  const setPlunoBodyLockState = (isActive) => {
    if (window.matchMedia('(max-width: 767px)').matches) {
      document.body?.classList.toggle(PLUNO_BODY_LOCK_CLASS, isActive);
    }
  };
  // ===== PLUNO TEMP END: body scroll-lock compatibility =====

  const getZendesk = () => (typeof window.zE === 'function' ? window.zE : null);

  const getSupportChatWindow = () => document.querySelector(WINDOW_TAG_NAME);

  const ensureZendeskScript = () => {
    if (getZendesk()) return Promise.resolve(true);
    if (zendeskLoadPromise) return zendeskLoadPromise;

    zendeskLoadPromise = new Promise((resolve) => {
      let settled = false;
      let timeoutId = null;
      let shouldAppendScript = false;
      let script = document.getElementById(ZENDESK_SCRIPT_ID)
        || document.querySelector(`script[src*="${ZENDESK_SCRIPT_KEY}"]`);

      const finish = (isReady) => {
        if (settled) return;

        settled = true;
        if (timeoutId !== null) window.clearTimeout(timeoutId);
        script?.removeEventListener('load', handleLoad);
        script?.removeEventListener('error', handleError);
        if (!isReady) zendeskLoadPromise = null;
        resolve(isReady);
      };

      const handleLoad = () => {
        script?.setAttribute('data-vl-support-chat-loaded', 'true');
        window.setTimeout(() => finish(Boolean(getZendesk())), 0);
      };

      const handleError = () => {
        if (script?.hasAttribute('data-vl-support-chat-script')) script.remove();
        finish(false);
      };

      if (script?.getAttribute('data-vl-support-chat-loaded') === 'true' || (script && getZendesk())) {
        finish(true);
        return;
      }

      if (!script) {
        script = document.createElement('script');
        script.id = ZENDESK_SCRIPT_ID;
        script.src = ZENDESK_SCRIPT_URL;
        script.async = true;
        script.setAttribute('data-vl-support-chat-script', '');
        shouldAppendScript = true;
      }

      script.addEventListener('load', handleLoad, { once: true });
      script.addEventListener('error', handleError, { once: true });
      timeoutId = window.setTimeout(() => finish(Boolean(getZendesk())), ZENDESK_LOAD_TIMEOUT);
      if (shouldAppendScript) document.head.append(script);
    });

    return zendeskLoadPromise;
  };

  const emitZendeskClose = () => {
    document.dispatchEvent(new CustomEvent(ZENDESK_CLOSE_EVENT));
  };

  const applyZendeskCustomization = () => {
    const zendesk = getZendesk();
    if (!zendesk || zendeskCustomizationApplied) return Boolean(zendesk);

    try {
      zendesk('messenger:set', 'customization', ZENDESK_CUSTOMIZATION);
      zendeskCustomizationApplied = true;
      return true;
    } catch (error) {
      return false;
    }
  };

  const bindZendeskCloseHandler = () => {
    const zendesk = getZendesk();
    if (!zendesk || zendeskCloseHandlerBound) return Boolean(zendesk);

    try {
      zendesk('messenger:on', 'close', () => {
        const activeZendesk = getZendesk();
        activeZendesk?.('messenger', 'hide');
        // ===== PLUNO TEMP CALL START =====
        setPlunoBodyLockState(false);
        // ===== PLUNO TEMP CALL END =====
        emitZendeskClose();
      });
      zendeskCloseHandlerBound = true;
      return true;
    } catch (error) {
      return false;
    }
  };

  class VLSupportChatLauncher extends HTMLElement {
    constructor() {
      super();

      this.isInitialized = false;
      this.isDialogOpen = false;
      this.isOpeningZendesk = false;
      this.dialogOpenedAt = null;
      this.zendeskOpenedAt = null;
      this.lastFocusedElement = null;

      this.handleClick = this.handleClick.bind(this);
      this.handleDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
      this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
      this.handleImageError = this.handleImageError.bind(this);
      this.handleZendeskClose = this.handleZendeskClose.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;

      this.isInitialized = true;
      this.classList.remove('is-zendesk-open');
      this.variant = this.dataset.variant === 'b' ? 'b' : 'a';
      this.trigger = this.querySelector('[data-vl-support-chat-trigger]');
      this.dialog = this.querySelector('[data-vl-support-chat-dialog]');
      this.backdrop = this.querySelector('[data-vl-support-chat-backdrop]');
      this.image = this.querySelector('.vl-support-chat-launcher__image');

      this.addEventListener('click', this.handleClick);
      this.image?.addEventListener('error', this.handleImageError);
      document.addEventListener(ZENDESK_CLOSE_EVENT, this.handleZendeskClose);

      // ===== PLUNO TEMP INIT START =====
      ensureZendeskScript().then((isReady) => {
        if (!isReady || !this.isConnected) return;

        const zendesk = getZendesk();
        try {
          applyZendeskCustomization();
        } catch (error) {
          return;
        }
        bindZendeskCloseHandler();
        // ===== PLUNO TEMP INIT END =====
      });
      this.emit('support_chat_exposure');
    }

    disconnectedCallback() {
      if (!this.isInitialized) return;

      this.closeDialog({ restoreFocus: false, trackClose: false });
      // ===== PLUNO TEMP DISCONNECT START =====
      setPlunoBodyLockState(false);
      // ===== PLUNO TEMP DISCONNECT END =====
      this.classList.remove('is-zendesk-open');
      this.removeEventListener('click', this.handleClick);
      this.image?.removeEventListener('error', this.handleImageError);
      document.removeEventListener(ZENDESK_CLOSE_EVENT, this.handleZendeskClose);

      this.trigger = null;
      this.dialog = null;
      this.backdrop = null;
      this.image = null;
      this.isInitialized = false;
    }

    handleClick(event) {
      const backdrop = event.target.closest('[data-vl-support-chat-backdrop]');
      if (backdrop && this.contains(backdrop)) {
        this.closeDialog({ closeMethod: 'backdrop' });
        return;
      }

      const trigger = event.target.closest('[data-vl-support-chat-trigger]');
      if (trigger && this.contains(trigger)) {
        this.emit('support_chat_launcher_click');

        event.preventDefault();
        this.isDialogOpen ? this.closeDialog({ closeMethod: 'launcher' }) : this.openDialog();
        return;
      }

      const chatTrigger = event.target.closest('[data-vl-support-chat-open]');
      if (chatTrigger && this.contains(chatTrigger)) {
        this.emit('support_chat_chat_click');
        this.closeDialog({ restoreFocus: false, trackClose: false });
        this.openZendesk(event);
        return;
      }

      const closeTrigger = event.target.closest('[data-vl-support-chat-close]');
      if (closeTrigger && this.contains(closeTrigger)) {
        this.closeDialog({ closeMethod: 'close_button' });
        return;
      }

      const callTrigger = event.target.closest('[data-vl-support-chat-call]');
      if (callTrigger && this.contains(callTrigger)) {
        this.emit('support_chat_call_click');
      }
    }

    openDialog() {
      if (!this.dialog || this.isDialogOpen) return;

      this.lastFocusedElement = document.activeElement;
      this.isDialogOpen = true;
      this.dialogOpenedAt = performance.now();
      this.classList.add('is-dialog-open');
      this.dialog.hidden = false;
      this.dialog.setAttribute('aria-hidden', 'false');
      this.dialog.setAttribute('aria-modal', window.matchMedia('(max-width: 767px)').matches ? 'true' : 'false');
      this.backdrop?.removeAttribute('hidden');
      this.trigger?.setAttribute('aria-expanded', 'true');

      document.addEventListener('pointerdown', this.handleDocumentPointerDown);
      document.addEventListener('keydown', this.handleDocumentKeydown);

      if (window.matchMedia('(max-width: 767px)').matches) {
        document.documentElement.classList.add('vl-support-chat-dialog-open');
      }

      this.querySelector('[data-vl-support-chat-open]')?.focus({ preventScroll: true });
    }

    closeDialog({ restoreFocus = true, trackClose = true, closeMethod = 'unknown' } = {}) {
      if (!this.dialog || !this.isDialogOpen) return;

      const openDurationMs = this.dialogOpenedAt === null
        ? null
        : Math.max(0, Math.round(performance.now() - this.dialogOpenedAt));
      this.isDialogOpen = false;
      this.dialogOpenedAt = null;
      this.classList.remove('is-dialog-open');
      this.dialog.hidden = true;
      this.dialog.setAttribute('aria-hidden', 'true');
      this.backdrop?.setAttribute('hidden', '');
      this.trigger?.setAttribute('aria-expanded', 'false');

      document.documentElement.classList.remove('vl-support-chat-dialog-open');
      document.removeEventListener('pointerdown', this.handleDocumentPointerDown);
      document.removeEventListener('keydown', this.handleDocumentKeydown);

      if (restoreFocus && this.lastFocusedElement instanceof HTMLElement) {
        this.lastFocusedElement.focus({ preventScroll: true });
      }

      this.lastFocusedElement = null;
      if (!trackClose) return;

      const closeDetail = {
        closeMethod,
        openDurationMs,
        thresholdMs: QUICK_CLOSE_THRESHOLD_MS,
      };
      this.emit('support_chat_menu_close', closeDetail);
    }

    handleDocumentPointerDown(event) {
      if (!this.isDialogOpen || this.contains(event.target)) return;
      this.closeDialog({ closeMethod: 'outside' });
    }

    handleDocumentKeydown(event) {
      if (!this.isDialogOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDialog({ closeMethod: 'escape' });
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = Array.from(this.dialog.querySelectorAll('a[href], button:not([disabled])'))
        .filter((element) => !element.hidden && element.getClientRects().length > 0);
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    async openZendesk(event) {
      event.preventDefault();
      if (this.isOpeningZendesk) return;

      this.isOpeningZendesk = true;

      try {
        await ensureZendeskScript();
        if (!this.isConnected) return;

        const zendesk = getZendesk();

        if (!zendesk) {
          this.openFallback();
          return;
        }

        bindZendeskCloseHandler();
        applyZendeskCustomization();
        const supportChatWindow = getSupportChatWindow();
        if (!supportChatWindow || typeof supportChatWindow.open !== 'function') {
          this.openFallback();
          return;
        }

        // ===== PLUNO TEMP OPEN START =====
        setPlunoBodyLockState(true);
        // ===== PLUNO TEMP OPEN END =====
        await supportChatWindow.open();
        this.classList.add('is-zendesk-open');
        this.zendeskOpenedAt = performance.now();
        this.emit('support_chat_zendesk_open');
      } catch (error) {
        // ===== PLUNO TEMP ERROR START =====
        setPlunoBodyLockState(false);
        // ===== PLUNO TEMP ERROR END =====
        this.openFallback();
      } finally {
        this.isOpeningZendesk = false;
      }
    }

    handleZendeskClose() {
      // ===== PLUNO TEMP CLOSE START =====
      setPlunoBodyLockState(false);
      // ===== PLUNO TEMP CLOSE END =====
      const openDurationMs = this.zendeskOpenedAt === null
        ? null
        : Math.max(0, Math.round(performance.now() - this.zendeskOpenedAt));
      this.zendeskOpenedAt = null;
      const closeDetail = {
        openDurationMs,
        thresholdMs: QUICK_CLOSE_THRESHOLD_MS,
      };
      if (openDurationMs !== null) {
        this.emit('support_chat_close', closeDetail);
        if (openDurationMs <= QUICK_CLOSE_THRESHOLD_MS) {
          this.emit('support_chat_quick_close', closeDetail);
        }
      }
      this.classList.remove('is-zendesk-open');
    }

    handleImageError() {
      this.classList.add('has-image-error');
    }

    openFallback() {
      const fallbackUrl = this.dataset.fallbackUrl;
      if (!fallbackUrl) return;

      window.location.assign(fallbackUrl);
    }

    emit(action, additionalDetail = {}) {
      const detail = {
        action,
        variant: this.variant,
        device: window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop',
        pageType: this.dataset.pageType || '',
        ...additionalDetail,
      };

      this.dispatchEvent(new CustomEvent('vl:support-chat-action', {
        bubbles: true,
        detail,
      }));

      if (typeof window.gtag === 'function') {
        window.gtag('event', action, {
          experiment_variant: detail.variant,
          device: detail.device,
          page_type: detail.pageType,
        });
      }
    }
  }

  customElements.define(TAG_NAME, VLSupportChatLauncher);

  class VLSupportChatWindow extends HTMLElement {
    constructor() {
      super();

      this.isInitialized = false;
      this.isOpen = false;
      this.renderPromise = null;
      this.lastFocusedElement = null;
      this.backdrop = null;
      this.navigationUnsubscribers = [];

      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;

      this.isInitialized = true;
      this.embed = this.querySelector('[data-vl-support-chat-embed]');
      this.loading = this.querySelector('[data-vl-support-chat-loading]');
      this.backdrop = this.querySelector('[data-vl-support-chat-window-backdrop]');
      this.backButton = this.querySelector('[data-vl-support-chat-window-back]');
      this.closeButton = this.querySelector('[data-vl-support-chat-window-close]');
      this.addEventListener('click', this.handleClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('keydown', this.handleKeydown);
      this.navigationUnsubscribers.forEach((unsubscribe) => unsubscribe?.());
      this.navigationUnsubscribers = [];
      this.backdrop = null;
      this.isInitialized = false;
    }

    bindNavigationEvents() {
      const zendesk = getZendesk();
      if (!zendesk || this.navigationUnsubscribers.length > 0) return;

      const showBackButton = () => this.setBackButtonVisible(true);

      try {
        this.navigationUnsubscribers = [
          zendesk('messenger:on', 'conversationOpened', showBackButton),
          zendesk('messenger:on', 'conversationClicked', showBackButton),
          zendesk('messenger:on', 'newConversationButtonClicked', showBackButton),
        ].filter((unsubscribe) => typeof unsubscribe === 'function');
      } catch (error) {
        this.navigationUnsubscribers = [];
      }
    }

    setBackButtonVisible(isVisible) {
      if (!this.backButton) return;

      this.backButton.hidden = !isVisible;
    }

    renderZendesk() {
      if (this.renderPromise) return this.renderPromise;

      this.renderPromise = new Promise((resolve, reject) => {
        const zendesk = getZendesk();
        if (!zendesk || !this.embed) {
          reject(new Error('Zendesk embedded target is unavailable.'));
          return;
        }

        if (!this.embed.id) this.embed.id = 'vl-support-chat-embed';
        this.bindNavigationEvents();

        try {
          zendesk('messenger', 'render', {
            mode: 'embedded',
            widget: {
              targetElement: `#${this.embed.id}`,
            },
          }, (error) => {
            if (error) {
              this.renderPromise = null;
              reject(error);
              return;
            }

            this.classList.add('is-ready');
            this.loading?.setAttribute('hidden', '');
            resolve(true);
          });
        } catch (error) {
          this.renderPromise = null;
          reject(error);
        }
      });

      return this.renderPromise;
    }

    async open() {
      if (this.isOpen) return this.renderZendesk();

      this.lastFocusedElement = document.activeElement;
      this.isOpen = true;
      this.hidden = false;
      document.documentElement.classList.add('vl-support-chat-window-open');
      document.addEventListener('keydown', this.handleKeydown);
      this.closeButton?.focus({ preventScroll: true });

      try {
        return await this.renderZendesk();
      } catch (error) {
        this.close();
        throw error;
      }
    }

    close() {
      if (!this.isOpen) return;

      this.isOpen = false;
      this.hidden = true;
      document.documentElement.classList.remove('vl-support-chat-window-open');
      document.removeEventListener('keydown', this.handleKeydown);
      emitZendeskClose();

      if (this.lastFocusedElement instanceof HTMLElement && this.lastFocusedElement.isConnected) {
        this.lastFocusedElement.focus({ preventScroll: true });
      }

      this.lastFocusedElement = null;
    }

    navigateToConversationList() {
      const zendesk = getZendesk();
      if (!zendesk || !this.renderPromise) return;

      try {
        zendesk('messenger:ui', 'navigation', {
          screen: 'ConversationList',
        });
        this.setBackButtonVisible(false);
      } catch (error) {
        // Keep the current conversation visible if navigation is unavailable.
      }
    }

    handleClick(event) {
      if (event.target.closest('[data-vl-support-chat-window-backdrop]')) {
        this.close();
        return;
      }

      if (event.target.closest('[data-vl-support-chat-window-back]')) {
        this.navigateToConversationList();
        return;
      }

      if (event.target.closest('[data-vl-support-chat-window-close]')) this.close();
    }

    handleKeydown(event) {
      if (event.key !== 'Escape') return;

      event.preventDefault();
      this.close();
    }

  }

  if (!customElements.get(WINDOW_TAG_NAME)) {
    customElements.define(WINDOW_TAG_NAME, VLSupportChatWindow);
  }
})();
